import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';

// SUNSARI LOGO - New branded logo
const SUNSARI_LOGO = "https://customer-assets.emergentagent.com/job_3cf82923-b1e0-42b8-bffd-6209a921b944/artifacts/rp3os92n_IMG_20260316_013209.png";

// PREMIUM SCANDINAVIAN COLOR PALETTE
const COLORS = {
  primary: '#F5F3EF',      // Soft warm white (primary background)
  secondary: '#D9D9D6',    // Light stone grey
  text: '#2F2F2F',         // Deep charcoal (primary text)
  accent: '#C97A2B',       // Deep saffron (primary accent)
  terracotta: '#B65A3A',   // Muted terracotta (secondary accent)
  olive: '#6B705C',        // Olive green (supporting accent)
  dark: '#1A1A1A',         // Near black
};

// Export for use in other components
const BRAND_COLOR = COLORS.accent;        // Deep saffron for transitions
const BRAND_COLOR_LIGHT = COLORS.terracotta;
const CREAM_COLOR = COLORS.primary;       // Soft warm white

// Page Transition Context
const PageTransitionContext = createContext();

export const usePageTransition = () => useContext(PageTransitionContext);

// Initial Page Load Animation - Premium Scandinavian Style
export const InitialLoadAnimation = ({ onComplete }) => {
  const [phase, setPhase] = useState('brand'); // 'brand' -> 'reveal' -> 'done'

  useEffect(() => {
    // Phase 1: Show brand color for 1000ms
    const timer1 = setTimeout(() => {
      setPhase('reveal');
    }, 1000);

    // Phase 2: Reveal animation takes 1200ms, then complete
    const timer2 = setTimeout(() => {
      setPhase('done');
      onComplete();
    }, 2500);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, [onComplete]);

  if (phase === 'done') return null;

  return (
    <motion.div 
      className="fixed inset-0 z-[99999] pointer-events-none"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Premium dark background to match new logo */}
      <motion.div
        className="absolute inset-0"
        style={{ backgroundColor: '#1A1A1A' }}
        initial={{ opacity: 1 }}
        animate={{ opacity: phase === 'reveal' ? 0 : 1 }}
        transition={{ duration: 0.6, delay: phase === 'reveal' ? 0.6 : 0 }}
      />

      {/* Golden shimmer effect */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(circle at 50% 50%, rgba(201, 122, 43, 0.15) 0%, transparent 60%)',
        }}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 2, opacity: [0, 1, 0] }}
        transition={{ duration: 2, delay: 0.5, ease: 'easeOut' }}
      />

      {/* Curved arch reveal */}
      <motion.div
        className="absolute inset-0"
        initial={{ clipPath: 'ellipse(0% 0% at 50% 100%)' }}
        animate={{ 
          clipPath: phase === 'reveal' 
            ? 'ellipse(200% 200% at 50% 100%)' 
            : 'ellipse(0% 0% at 50% 100%)'
        }}
        transition={{ 
          duration: 1.4, 
          ease: [0.76, 0, 0.24, 1]
        }}
      >
        <div className="absolute inset-0" style={{ backgroundColor: COLORS.primary }} />
      </motion.div>

      {/* Center logo with premium entrance animation */}
      <motion.div
        className="absolute inset-0 flex flex-col items-center justify-center"
        initial={{ opacity: 1 }}
        animate={{ opacity: phase === 'reveal' ? 0 : 1 }}
        transition={{ duration: 0.4 }}
      >
        {/* Rotating golden ring around the round logo */}
        <motion.div
          className="relative"
          initial={{ rotate: 0 }}
          animate={{ rotate: 360 }}
          transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
        >
          <div 
            className="absolute inset-0 rounded-full"
            style={{
              width: '220px',
              height: '220px',
              marginLeft: '-110px',
              marginTop: '-110px',
              left: '50%',
              top: '50%',
              border: '2px solid transparent',
              borderTopColor: '#C97A2B',
              borderRightColor: '#C97A2B',
            }}
          />
        </motion.div>
        
        {/* Round logo with scale and glow animation */}
        <motion.div
          className="relative"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        >
          <div 
            className="w-40 h-40 md:w-48 md:h-48 rounded-full overflow-hidden"
            style={{ 
              boxShadow: '0 0 40px rgba(201, 122, 43, 0.5)',
            }}
          >
            <img 
              src={SUNSARI_LOGO}
              alt="Sunsari"
              className="w-full h-full object-cover"
            />
          </div>
        </motion.div>
        
        {/* Animated loading dots */}
        <motion.div 
          className="flex gap-2 mt-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              className="w-2 h-2 rounded-full bg-[#C97A2B]"
              animate={{ 
                scale: [1, 1.5, 1],
                opacity: [0.5, 1, 0.5]
              }}
              transition={{ 
                duration: 0.8, 
                delay: i * 0.15, 
                repeat: Infinity,
                ease: 'easeInOut'
              }}
            />
          ))}
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

// Page Transition Overlay for navigation between pages
export const PageTransitionOverlay = ({ isAnimating, onComplete }) => {
  return (
    <AnimatePresence mode="wait">
      {isAnimating && (
        <motion.div 
          className="fixed inset-0 z-[99999] pointer-events-auto"
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Dark background to match new logo */}
          <motion.div
            className="absolute inset-0"
            style={{ backgroundColor: '#1A1A1A' }}
            initial={{ y: '100%' }}
            animate={{ y: '0%' }}
            exit={{ y: '-100%' }}
            transition={{ 
              duration: 0.7, 
              ease: [0.76, 0, 0.24, 1],
            }}
          />
          
          {/* Golden shimmer ring */}
          <motion.div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: 'radial-gradient(circle at 50% 50%, rgba(201, 122, 43, 0.2) 0%, transparent 50%)',
            }}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 3, opacity: [0, 1, 0] }}
            transition={{ duration: 1.2, delay: 0.3, ease: 'easeOut' }}
          />
          
          {/* Curved arch reveal */}
          <motion.div
            className="absolute inset-0 overflow-hidden"
            initial={{ clipPath: 'ellipse(0% 0% at 50% 100%)' }}
            animate={{ clipPath: 'ellipse(200% 200% at 50% 100%)' }}
            transition={{ duration: 1.1, delay: 0.5, ease: [0.76, 0, 0.24, 1] }}
            onAnimationComplete={onComplete}
          >
            <div className="absolute inset-0" style={{ backgroundColor: COLORS.primary }} />
          </motion.div>
          
          {/* Center round logo */}
          <motion.div
            className="absolute inset-0 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <motion.div
              className="w-36 h-36 md:w-44 md:h-44 rounded-full overflow-hidden"
              style={{ boxShadow: '0 0 40px rgba(201, 122, 43, 0.5)' }}
              initial={{ y: 30, opacity: 0, scale: 0.85 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: -30, opacity: 0 }}
              transition={{ duration: 0.6, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
            >
              <img 
                src={SUNSARI_LOGO}
                alt="Sunsari"
                className="w-full h-full object-cover"
              />
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Page Transition Provider
export const PageTransitionProvider = ({ children }) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [nextPath, setNextPath] = useState(null);
  const [showInitialLoad, setShowInitialLoad] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const navigateWithTransition = useCallback((path) => {
    if (path === location.pathname) return;
    
    setNextPath(path);
    setIsAnimating(true);
  }, [location.pathname]);

  const handleTransitionComplete = useCallback(() => {
    if (nextPath) {
      navigate(nextPath);
      setTimeout(() => {
        setIsAnimating(false);
        setNextPath(null);
        window.scrollTo(0, 0);
      }, 100);
    }
  }, [nextPath, navigate]);

  const handleInitialLoadComplete = useCallback(() => {
    setShowInitialLoad(false);
  }, []);

  return (
    <PageTransitionContext.Provider value={{ navigateWithTransition, isAnimating, brandColor: BRAND_COLOR }}>
      <AnimatePresence>
        {showInitialLoad && (
          <InitialLoadAnimation onComplete={handleInitialLoadComplete} />
        )}
      </AnimatePresence>
      <PageTransitionOverlay isAnimating={isAnimating} onComplete={handleTransitionComplete} />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: showInitialLoad ? 0 : 1 }}
        transition={{ duration: 0.6 }}
      >
        {children}
      </motion.div>
    </PageTransitionContext.Provider>
  );
};

// Export brand color for use in other components
export const SUNSARI_BRAND_COLOR = BRAND_COLOR;
export const SUNSARI_CREAM = CREAM_COLOR;
export const SUNSARI_COLORS = COLORS;
export const SUNSARI_LOGO_URL = SUNSARI_LOGO;

// Animated text reveal component
export const TextReveal = ({ children, delay = 0, className = '' }) => {
  return (
    <motion.div
      className={`overflow-hidden ${className}`}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-50px" }}
    >
      <motion.div
        variants={{
          hidden: { y: '100%', opacity: 0 },
          visible: { 
            y: 0, 
            opacity: 1,
            transition: {
              duration: 0.8,
              delay,
              ease: [0.76, 0, 0.24, 1]
            }
          }
        }}
      >
        {children}
      </motion.div>
    </motion.div>
  );
};

// Hero Arch Frame with PREMIUM SCANDINAVIAN ANIMATION - MOBILE OPTIMIZED
export const HeroArchFrame = ({ src, alt }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  useEffect(() => {
    // Trigger animation after a small delay to ensure it plays
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <motion.div 
      className="relative w-full max-w-3xl mx-auto px-4 md:px-0"
      initial={{ opacity: 1 }}
      animate={{ opacity: 1 }}
    >
      <div className="relative aspect-[3/4] md:aspect-[3/4]">
        {/* Outermost saffron accent border */}
        <motion.div 
          className="absolute -inset-1 border border-[#C97A2B]/30 hidden md:block"
          style={{ borderRadius: '50% 50% 0 0 / 28% 28% 0 0' }}
          initial={{ opacity: 0, scale: 1.15 }}
          animate={isLoaded ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: isMobile ? 0.8 : 1.5, delay: isMobile ? 0.1 : 0.2, ease: [0.22, 1, 0.36, 1] }}
        />
        
        {/* Stone grey decorative border */}
        <motion.div 
          className="absolute inset-0 border-2 border-[#D9D9D6]"
          style={{ borderRadius: '50% 50% 0 0 / 30% 30% 0 0' }}
          initial={{ opacity: 0, scale: 1.1 }}
          animate={isLoaded ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: isMobile ? 0.6 : 1.3, delay: isMobile ? 0.2 : 0.4, ease: [0.22, 1, 0.36, 1] }}
        />
        
        {/* Inner thin saffron line - hidden on mobile */}
        <motion.div 
          className="absolute inset-2 border border-[#C97A2B]/20 hidden md:block"
          style={{ borderRadius: '50% 50% 0 0 / 32% 32% 0 0' }}
          initial={{ opacity: 0 }}
          animate={isLoaded ? { opacity: 1 } : {}}
          transition={{ duration: isMobile ? 0.5 : 1, delay: isMobile ? 0.3 : 0.7, ease: [0.22, 1, 0.36, 1] }}
        />
        
        {/* Main arch container */}
        <div 
          className="absolute inset-2 md:inset-4 overflow-hidden"
          style={{ borderRadius: '50% 50% 0 0 / 35% 35% 0 0' }}
        >
          {/* Premium dual curtain reveal - Simplified on mobile */}
          <motion.div
            className="absolute inset-0 z-30 flex pointer-events-none"
          >
            {/* Left curtain - slides out */}
            <motion.div
              className="w-1/2 h-full origin-left"
              style={{ backgroundColor: COLORS.secondary }}
              initial={{ scaleX: 1 }}
              animate={isLoaded ? { scaleX: 0 } : {}}
              transition={{ duration: isMobile ? 1 : 1.6, delay: isMobile ? 0.3 : 0.8, ease: [0.76, 0, 0.24, 1] }}
            />
            {/* Right curtain - slides out */}
            <motion.div
              className="w-1/2 h-full origin-right"
              style={{ backgroundColor: COLORS.secondary }}
              initial={{ scaleX: 1 }}
              animate={isLoaded ? { scaleX: 0 } : {}}
              transition={{ duration: isMobile ? 1 : 1.6, delay: isMobile ? 0.3 : 0.8, ease: [0.76, 0, 0.24, 1] }}
            />
          </motion.div>
          
          {/* Saffron sheen overlay - only on desktop */}
          {!isMobile && (
            <motion.div
              className="absolute inset-0 z-20 pointer-events-none hidden md:block"
              style={{ 
                background: 'linear-gradient(90deg, transparent 0%, rgba(201,122,43,0.25) 50%, transparent 100%)',
                width: '200%',
                left: '-100%'
              }}
              initial={{ x: 0 }}
              animate={isLoaded ? { x: '100%' } : {}}
              transition={{ duration: 1.2, delay: 2, ease: [0.22, 1, 0.36, 1] }}
            />
          )}
          
          {/* Image with parallax zoom effect */}
          <motion.img
            src={src}
            alt={alt}
            className="w-full h-full object-cover"
            initial={{ scale: isMobile ? 1.2 : 1.5, filter: 'brightness(0.6) saturate(0.8)' }}
            animate={isLoaded ? { scale: 1.05, filter: 'brightness(1) saturate(1)' } : {}}
            transition={{ duration: isMobile ? 1.5 : 2.5, delay: isMobile ? 0.3 : 0.8, ease: [0.22, 1, 0.36, 1] }}
          />
          
          {/* Subtle vignette overlay */}
          <div 
            className="absolute inset-0 pointer-events-none"
            style={{ 
              background: 'radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.1) 100%)'
            }}
          />
        </div>
        
        {/* Corner ornaments - only on desktop */}
        <motion.div
          className="absolute top-6 left-6 w-8 h-8 border-t-2 border-l-2 border-[#C97A2B]/40 hidden md:block"
          initial={{ opacity: 0, scale: 0 }}
          animate={isLoaded ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.8, delay: 2.2, ease: [0.22, 1, 0.36, 1] }}
        />
        <motion.div
          className="absolute top-6 right-6 w-8 h-8 border-t-2 border-r-2 border-[#C97A2B]/40 hidden md:block"
          initial={{ opacity: 0, scale: 0 }}
          animate={isLoaded ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.8, delay: 2.3, ease: [0.22, 1, 0.36, 1] }}
        />
      </div>
    </motion.div>
  );
};

// Architectural Arch Frame Component with Premium Scandinavian animation
export const ArchFramedImage = ({ src, alt, className = '' }) => {
  const uniqueId = `arch-${Math.random().toString(36).substr(2, 9)}`;
  
  return (
    <motion.div 
      className={`relative ${className}`}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
    >
      {/* The arch frame container */}
      <div className="relative w-full h-full overflow-hidden">
        {/* SVG Arch Mask */}
        <svg 
          className="absolute inset-0 w-full h-full" 
          viewBox="0 0 100 140" 
          preserveAspectRatio="none"
          style={{ zIndex: 2 }}
        >
          <defs>
            <mask id={uniqueId}>
              <rect width="100" height="140" fill="white" />
              <path 
                d="M 8 140 L 8 45 Q 50 5 92 45 L 92 140 Z" 
                fill="black"
              />
            </mask>
          </defs>
          <rect 
            width="100" 
            height="140" 
            fill="#F5F3EF"
            mask={`url(#${uniqueId})`}
          />
        </svg>
        
        {/* Door reveal animation - splits in the middle */}
        <motion.div
          className="absolute inset-0 z-10 flex overflow-hidden"
          style={{ borderRadius: '50% 50% 0 0 / 35% 35% 0 0' }}
        >
          {/* Left door panel */}
          <motion.div
            className="w-1/2 h-full"
            style={{ backgroundColor: COLORS.secondary, transformOrigin: 'left' }}
            variants={{
              hidden: { scaleX: 1 },
              visible: { 
                scaleX: 0,
                transition: { duration: 1.2, delay: 0.3, ease: [0.76, 0, 0.24, 1] }
              }
            }}
          />
          {/* Right door panel */}
          <motion.div
            className="w-1/2 h-full"
            style={{ backgroundColor: COLORS.secondary, transformOrigin: 'right' }}
            variants={{
              hidden: { scaleX: 1 },
              visible: { 
                scaleX: 0,
                transition: { duration: 1.2, delay: 0.3, ease: [0.76, 0, 0.24, 1] }
              }
            }}
          />
        </motion.div>
        
        {/* The actual image with zoom effect */}
        <motion.img
          src={src}
          alt={alt}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ zIndex: 1 }}
          variants={{
            hidden: { scale: 1.3, filter: 'brightness(0.7)' },
            visible: { 
              scale: 1,
              filter: 'brightness(1)',
              transition: { duration: 1.8, delay: 0.3, ease: [0.22, 1, 0.36, 1] }
            }
          }}
        />
      </div>
    </motion.div>
  );
};

// Dark background version of arch frame
export const ArchFramedImageDark = ({ src, alt, className = '' }) => {
  return (
    <motion.div 
      className={`relative ${className}`}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
    >
      <div className="relative w-full h-full overflow-hidden">
        <svg 
          className="absolute inset-0 w-full h-full" 
          viewBox="0 0 100 140" 
          preserveAspectRatio="none"
          style={{ zIndex: 2 }}
        >
          <defs>
            <mask id={`arch-mask-dark-${src?.slice(-10) || Math.random()}`}>
              <rect width="100" height="140" fill="white" />
              <path 
                d="M 8 140 L 8 45 Q 50 5 92 45 L 92 140 Z" 
                fill="black"
              />
            </mask>
          </defs>
          <rect 
            width="100" 
            height="140" 
            fill="#1A1A1A"
            mask={`url(#arch-mask-dark-${src?.slice(-10) || Math.random()})`}
          />
        </svg>
        
        {/* Reveal overlay */}
        <motion.div
          className="absolute inset-0 z-[3]"
          style={{ backgroundColor: COLORS.secondary }}
          variants={{
            hidden: { scaleY: 1 },
            visible: { 
              scaleY: 0,
              transition: { duration: 1.2, ease: [0.76, 0, 0.24, 1] }
            }
          }}
          initial={{ transformOrigin: 'top' }}
        />
        
        <motion.img
          src={src}
          alt={alt}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ zIndex: 1 }}
          variants={{
            hidden: { scale: 1.3 },
            visible: { 
              scale: 1,
              transition: { duration: 1.6, ease: [0.22, 1, 0.36, 1] }
            }
          }}
        />
      </div>
    </motion.div>
  );
};

// Image with reveal animation (no arch)
export const ImageReveal = ({ src, alt, className = '' }) => {
  return (
    <motion.div 
      className={`relative overflow-hidden ${className}`}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
    >
      {/* Stone grey reveal overlay */}
      <motion.div
        className="absolute inset-0 z-10"
        style={{ backgroundColor: COLORS.secondary }}
        variants={{
          hidden: { scaleY: 1 },
          visible: { 
            scaleY: 0,
            transition: { duration: 1.2, ease: [0.76, 0, 0.24, 1] }
          }
        }}
        initial={{ transformOrigin: 'top' }}
      />
      
      {/* Image with scale animation */}
      <motion.img
        src={src}
        alt={alt}
        className="w-full h-full object-cover"
        variants={{
          hidden: { scale: 1.3 },
          visible: { 
            scale: 1,
            transition: { duration: 1.5, ease: [0.22, 1, 0.36, 1] }
          }
        }}
      />
    </motion.div>
  );
};

export default PageTransitionProvider;
